/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import toNumber from 'lodash/toNumber';
import forEach from 'lodash/forEach';
import filter from 'lodash/filter';

import {
    Select,
    SecurityHelper,
    Box,
    StyledModal,
    ModalActions,
    ButtonGroup,
    Button,
    Stack,
    Text,
    Alert,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';

import { gnoment } from 'shared-ui/gnoment';
import { RecaptchaCheckbox, withRecaptchaFallback } from 'shared-ui/recaptcha';
import { SharedCaptchaModal } from 'shared-ui/components/RecaptchaModal/SharedCaptchaModal';
import { authModalActions } from 'shared-ui/auth';
import WeeklyMotorAvailability from '../components/WeeklyMotorAvailability';
import * as pActions from '../actions/permit';
import AvailabilityNavigator from '../components/AvailabilityNavigator';
import * as aActions from '../actions/availability';
import BookNowComponent from '../components/BookNowComponent';
import * as types from '../constants';
import { formatErrorMessage, issuanceStatuses } from '../utils/permitUtilsNew';
import {
    getGateA,
    RECAPTCHA_ACTION_CREATE_ISSUANCE,
    RECAPTCHA_FAILED_MESSAGE,
} from '../utils/recaptcha';

const permitCountOptions = [
    {
        label: '1',
        value: '1',
    },
    {
        label: '2',
        value: '2',
    },
    {
        label: '3',
        value: '3',
    },
    {
        label: '4',
        value: '4',
    },
    {
        label: '5',
        value: '5',
    },
    {
        label: '6',
        value: '6',
    },
    {
        label: '7',
        value: '7',
    },
];

export class AvailabilityDayUseMotorContainer extends React.Component {
    static displayName = 'AvailabilityDayUseMotorContainer';

    static propTypes = {
        router: COMPAT_ROUTER_PROP_TYPES,
        permitId: PropTypes.string,
        permitDate: PropTypes.string,
        availabilities: PropTypes.object,
        divisions: PropTypes.object,
        selectedStartDate: PropTypes.string,
        selectedEndDate: PropTypes.string,
        selectedDivision: PropTypes.object,
        divisionSearchCriteria: PropTypes.object,
        dispatch: PropTypes.func,
        issuanceLoading: PropTypes.bool,
        isSmallerGrid: PropTypes.bool,
        isFullView: PropTypes.bool,
        account: PropTypes.object,
        handleDateChange: PropTypes.func,
        existingIssuance: PropTypes.any,
        recaptcha: PropTypes.object,
    };

    constructor(props) {
        super(props);
        this.state = {
            permitId: props.permitId,
            permitDate: props.permitDate,
            divisions: this.getFilteredDivisions(
                props.divisions,
                props.divisionSearchCriteria
            ),
            isCountModalOpen: false,
            permitCount: '1',
            isMessageModalOpen: false,
            message: null,
            messageTitle: null,
            waitingForUserAccount: false,
            isChallengeModalOpen: false,
        };
        this.onNavigatorDateChanged = this.onNavigatorDateChanged.bind(this);
        this.onSelectToggle = this.onSelectToggle.bind(this);
        this.cancelPermitCountModal = this.cancelPermitCountModal.bind(this);
        this.applyPermitCountModal = this.applyPermitCountModal.bind(this);
        this.handlePermitCountChange = this.handlePermitCountChange.bind(this);
        this.onCloseMessageModal = this.onCloseMessageModal.bind(this);
        this.onClearDates = this.onClearDates.bind(this);
        this.onBook = this.onBook.bind(this);
    }

    componentDidMount() {
        this.refreshAvailability();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (
            nextProps.permitDate !== this.state.permitDate ||
            JSON.stringify(nextProps.divisions) !==
                JSON.stringify(this.props.divisions) ||
            JSON.stringify(nextProps.divisionSearchCriteria) !==
                JSON.stringify(this.props.divisionSearchCriteria)
        ) {
            this.setState(
                {
                    permitId: nextProps.permitId,
                    permitDate: nextProps.permitDate,
                    divisions: this.getFilteredDivisions(
                        nextProps.divisions,
                        nextProps.divisionSearchCriteria
                    ),
                },
                () => {
                    this.refreshAvailability();
                }
            );
        }
    }

    componentDidUpdate(prevProps) {
        if (
            SecurityHelper.isLoggedIn() &&
            this.state.waitingForUserAccount &&
            this.props.account &&
            prevProps.account !== this.props.account
        ) {
            this.setState({ waitingForUserAccount: false });
            this.onBook();
        }
    }

    handlePermitCountChange(value) {
        this.setState({ permitCount: value });
    }

    onCloseMessageModal() {
        this.setState({
            isMessageModalOpen: false,
            message: null,
            messageTitle: null,
        });
    }

    onClearDates() {
        this.props.dispatch(pActions.setSelectedStartDate(null));
        this.props.dispatch(pActions.setSelectedEndDate(null));
    }

    onSelectToggle(division, startDate, endDate) {
        if (
            this.props.selectedStartDate === startDate &&
            this.props.selectedDivision &&
            this.props.selectedDivision.id === division.id
        ) {
            this.props.dispatch(pActions.setSelectedStartDate(null));
            this.props.dispatch(pActions.setSelectedEndDate(null));
        } else {
            this.props.dispatch(pActions.setSelectedStartDate(startDate));
            this.props.dispatch(pActions.setSelectedEndDate(endDate));
        }
        this.props.dispatch(pActions.setSelectedDivision(division));
    }

    onNavigatorDateChanged(dates) {
        this.props.handleDateChange(dates.startDate);
    }

    async onBook() {
        const loggedIn = SecurityHelper.isLoggedIn();
        if (!loggedIn) {
            this.setState({ waitingForUserAccount: true });
            this.props.dispatch(authModalActions.openLoginModal());
            return;
        }
        // A v2 challenge was previously required - show the checkbox modal before
        // resubmitting. Once open, fall through and submit the v2 token.
        if (
            this.props.recaptcha.isChallengeRequired &&
            !this.state.isChallengeModalOpen
        ) {
            this.setState({ isChallengeModalOpen: true });
            return;
        }
        const permitId = types.BOUNDARY_WATERS_PERMIT_ID;
        const items = [];
        for (let i = 0; i < toNumber(this.state.permitCount); i += 1) {
            const item = {
                permit_id: permitId,
                permit_type: types.TYPE_DAYUSE_MOTOR,
                division_id: this.props.selectedDivision.id,
                user_id: SecurityHelper.getAccount().account_id,
                start_date: gnoment(this.props.selectedStartDate),
                status: issuanceStatuses.get(types.PERMIT_PRELIMINARY),
                equipments: [
                    {
                        type: types.EQUIP_TYPE_MOTORBOAT,
                        qty: 1,
                    },
                ],
            };
            items.push(item);
        }

        const gate_a = await getGateA(
            this.props.recaptcha,
            RECAPTCHA_ACTION_CREATE_ISSUANCE
        );
        this.props.dispatch({
            type: types.SET_ISSUANCE_LOADING,
            issuanceLoading: true,
        });
        let issuanceIds;
        try {
            issuanceIds = await pActions.addItems(items, gate_a);
        } catch (error) {
            const { shouldRetry, isFailed } =
                this.props.recaptcha.handleRecaptchaError(error);
            if (shouldRetry) {
                // Challenge required - pop the v2 checkbox modal for the retry.
                this.setState({ isChallengeModalOpen: true });
                return;
            }
            if (isFailed) {
                // Hard reCAPTCHA denial - do not auto-retry.
                this.setState({
                    isChallengeModalOpen: false,
                    isMessageModalOpen: true,
                    message: RECAPTCHA_FAILED_MESSAGE,
                    messageTitle: 'Error',
                });
                return;
            }

            let msg = `${error}`;
            if (error.response && error.response.data && error.response.data.error) {
                msg = `${error.response.data.error}`;
            }
            const errStr = formatErrorMessage(msg);
            this.setState({
                isChallengeModalOpen: false,
                isMessageModalOpen: true,
                message: errStr,
                messageTitle: 'Error',
            });
            return;
        } finally {
            this.props.dispatch({
                type: types.SET_ISSUANCE_LOADING,
                issuanceLoading: false,
            });
        }

        this.setState({ isChallengeModalOpen: false });
        const reservationIds = [];
        issuanceIds.forEach((id) => {
            reservationIds.push(`${id}${types.RES_ID_DELIMITER}${permitId}`);
        });

        const idsStr = reservationIds.join(',');
        const redirectURL = `/permits/${permitId}/registration/${idsStr}/select-cooperator`;
        this.props.router.navigate(redirectURL);
    }

    getFilteredDivisions(divisionObjs, searchCriteria) {
        const ret = [];
        Object.keys(divisionObjs).forEach((id) => {
            const division = divisionObjs[id];
            if (division.type === types.DIV_TYPE_DM) {
                ret[ret.length] = divisionObjs[id];
            }
        });
        return this.applySearchCriteria(ret, searchCriteria);
    }

    handleDivisionDetailsClick = (divisionId) => {
        this.props.dispatch(pActions.openDivisionDetailsModal(divisionId));
    };

    applySearchCriteria(divisionObjs, searchCriteria) {
        let ret = divisionObjs;
        if (searchCriteria) {
            if (searchCriteria.searchText && searchCriteria.searchText !== '') {
                const searchText = searchCriteria.searchText.toLowerCase();
                ret = ret.filter((item) =>
                    `${item.name}  ${item.district}`.toLowerCase().includes(searchText)
                );
            }
            if (searchCriteria.motorSize) {
                ret = ret.filter((item) => {
                    return item.motor_hp_limit <= searchCriteria.motorSize;
                });
            }
            if (searchCriteria.areas.length > 0) {
                const retFiltered = [];
                forEach(ret, (item) => {
                    filter(
                        searchCriteria.areas,
                        (area) => item.district.includes(area) && retFiltered.push(item)
                    );
                    return retFiltered;
                });
                ret = retFiltered;
            }
        }
        return ret;
    }

    refreshAvailability() {
        if (this.state.permitId && this.state.permitDate) {
            const startDate = gnoment(this.state.permitDate).clone();
            this.props.dispatch(
                aActions.fetchAvailability(
                    this.state.permitId,
                    startDate.clone().startOf('isoWeek').add(-2, 'day').toDate(),
                    startDate
                        .clone()
                        .startOf('isoWeek')
                        .add(-2, 'day')
                        .add(28, 'day')
                        .toDate(),
                    startDate.clone().utc().startOf('day').toISOString()
                )
            );
        }
    }

    cancelPermitCountModal() {
        this.setState({ isCountModalOpen: false });
    }

    applyPermitCountModal() {
        this.setState({ isCountModalOpen: false });
    }

    renderAvailabilityNavigator = () => {
        const { permitDate } = this.props;
        return (
            <AvailabilityNavigator
                startDate={gnoment(permitDate)}
                endDate={gnoment(permitDate).add(10, 'day')}
                onDatesChange={this.onNavigatorDateChanged}
                handleClearDatesClick={this.onClearDates}
                weeksToSkip={4}
            />
        );
    };

    render() {
        if (this.props.existingIssuance) {
            return (
                <Alert type="warning" hasAriaLive>
                    <p>
                        Modifying an existing reservation for{' '}
                        <strong>Day Use Motor permits</strong> is not allowed, you must
                        remove this reservation from your cart and recreate it.
                    </p>
                </Alert>
            );
        }

        let modalMessages = null;
        if (this.state.message) {
            modalMessages = [];
            const arr = this.state.message.split('\n');
            arr.forEach((msg, index) => {
                modalMessages.push(<li key={index}>{msg}</li>);
            });
        }

        return (
            <div className="per-availability-content">
                <p>
                    Any group entering for the day to the BWCAW from May 1 - September 30
                    is required to obtain a quota permit. Motor weeks are Saturday through
                    Friday. A day use motor reservation covers a period of one week
                    (Saturday to Friday). It is valid only for the one day you specify
                    when the permit is picked up. If you wish to enter the BWCAW on more
                    than one day in a week, you must make another reservation.
                </p>
                <p>
                    Select permit type and entry date from the filters above to begin.
                    Click on filter box to narrow search.
                </p>

                {this.renderAvailabilityNavigator()}

                <div className="per-availability-table-container">
                    <WeeklyMotorAvailability
                        divisions={this.state.divisions}
                        permitDate={this.props.permitDate}
                        availabilities={this.props.availabilities}
                        onSelectToggle={this.onSelectToggle}
                        onDivisionDetailsClick={this.handleDivisionDetailsClick}
                        isSmallerGrid={this.props.isSmallerGrid}
                        isFullView={this.props.isFullView}
                        selectedDivision={this.props.selectedDivision}
                        selectedStartDate={this.props.selectedStartDate}
                        selectedEndDate={this.props.selectedEndDate}
                    >
                        <Box className="pt-1" background="white">
                            {this.renderAvailabilityNavigator()}
                        </Box>
                    </WeeklyMotorAvailability>
                </div>
                <BookNowComponent
                    selectedDivision={this.props.selectedDivision}
                    selectedStartDate={this.props.selectedStartDate}
                    selectedEndDate={this.props.selectedEndDate}
                    onBookClick={() => {
                        this.onBook();
                    }}
                    isEnabled={
                        !this.props.issuanceLoading &&
                        this.props.selectedDivision !== null &&
                        this.props.selectedStartDate !== null
                    }
                />

                <StyledModal
                    shouldShowCloseButton={false}
                    shouldActionsStickInMobile
                    size="md"
                    isOpen={this.state.isCountModalOpen}
                    heading="How many permits do you need?"
                    onRequestClose={this.cancelPermitCountModal}
                >
                    <Stack>
                        <Text>
                            Each permit is valid for one day of the week of:{' '}
                            <strong>
                                {this.props.selectedStartDate &&
                                    gnoment(this.props.selectedStartDate)
                                        .utc()
                                        .format('ll')}{' '}
                                -{' '}
                                {this.props.selectedEndDate &&
                                    gnoment(this.props.selectedEndDate)
                                        .utc()
                                        .format('ll')}
                            </strong>
                            . You will need a permit for each day. The valid day will be
                            determined upon issuing the permit, so you don&#39;t need to
                            choose the specific day during booking.
                        </Text>

                        <Select
                            id="how-many-permits-for-week"
                            label="How many permits do you want for this week?"
                            placeholder="Select an option..."
                            options={permitCountOptions}
                            onChange={this.handlePermitCountChange}
                            value={this.state.permitCount}
                        />
                    </Stack>

                    <ModalActions>
                        <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                            <Button
                                onClick={this.cancelPermitCountModal}
                                appearance="tertiary"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={this.applyPermitCountModal}
                                aria-label="Apply"
                            >
                                Apply
                            </Button>
                        </ButtonGroup>
                    </ModalActions>
                </StyledModal>

                <StyledModal
                    shouldShowCloseButton={false}
                    shouldActionsStickInMobile
                    size="md"
                    isOpen={this.state.isMessageModalOpen}
                    heading={this.state.messageTitle}
                    onRequestClose={this.onCloseMessageModal}
                >
                    {modalMessages}

                    <ModalActions>
                        <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                            <Button
                                onClick={this.onCloseMessageModal}
                                appearance="tertiary"
                            >
                                Close
                            </Button>
                        </ButtonGroup>
                    </ModalActions>
                </StyledModal>

                <SharedCaptchaModal
                    isOpen={this.state.isChallengeModalOpen}
                    onRequestClose={() => this.setState({ isChallengeModalOpen: false })}
                    onContinue={this.onBook}
                    heading="Confirm You're Not a Robot"
                    showCancelButton
                    isContinueDisabled={this.props.recaptcha.isV2Invalid}
                    isContinueLoading={this.props.issuanceLoading}
                >
                    Please complete the challenge below to continue with your reservation.
                    <RecaptchaCheckbox
                        {...this.props.recaptcha.recaptchaCheckboxProps}
                        isFallbackOnly
                        action={RECAPTCHA_ACTION_CREATE_ISSUANCE}
                    />
                </SharedCaptchaModal>
            </div>
        );
    }
}

function select(state) {
    return {
        account: state.auth.account,
        permitId: state.boundarywaters.permit.result.id,
        permitDate: state.boundarywaters.permitDate,
        selectedDivision: state.boundarywaters.selectedDivision,
        selectedStartDate: state.boundarywaters.selectedStartDate,
        selectedEndDate: state.boundarywaters.selectedEndDate,
        availabilities: state.boundarywaters.permit.availabilities,
        permit: state.boundarywaters.permit.result,
        divisions: state.boundarywaters.permit.result.divisions,
        issuance: state.boundarywaters.permit.issuance,
        existingIssuance: state.boundarywaters.permit.existingIssuance,
        error: state.boundarywaters.permit.error,
        divisionSearchCriteria: state.boundarywaters.divisionSearchCriteria,
        issuanceLoading: state.boundarywaters.issuanceLoading,
    };
}

export default withCompatRouter(
    connect(select)(withRecaptchaFallback(AvailabilityDayUseMotorContainer))
);

// cSpell:ignore boundarywaters, dayuse, bwcaw
